/*
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 *
 * Copyright 2024 UNESP Universidade Estadual Paulista "Júlio de Mesquita Filho"
 * Copyright 2024-2026 UNICAMP Universidade Estadual de Campinas
 *
 */

import { ROLE_WILDCARD_PUBLIC, UnespCoreMenuItem } from 'src/libs/unesp-core'

export const getMenu = (logged: boolean, perfis: string[]): UnespCoreMenuItem[] => {
  const menu: UnespCoreMenuItem[] = [
    {
      displayName: 'Concursos',
      iconName: 'edit_calendar',
      roles: ['RH', 'DTA', 'CCPT', 'CACP', 'FINANCAS', 'DIRETOR', 'MULTIUNIDADES', 'DEPARTAMENTO'],
      order: 1,
      route: '/concurso',
    },
    {
      displayName: 'Inscrições',
      iconName: 'list_alt',
      roles: ['RH', 'DTA', 'FINANCAS', 'CCPT', 'CACP', 'DEPARTAMENTO', 'MULTIUNIDADES', 'DIRETOR'],
      order: 2,
      route: '/inscricao',
    },
    {
      displayName: 'Recursos',
      iconName: 'gavel',
      roles: ['RH', 'DEPARTAMENTO', 'DIRETOR'],
      order: 3,
      route: '/recurso',
    },
    // {
    //   displayName: 'Usuários externo',
    //   iconName: 'manage_accounts',
    //   roles: ['RH', 'DTA'],
    //   order: 4,
    //   route: '/usuario-externo',
    // },
    {
      displayName: 'Configurações',
      iconName: 'settings',
      roles: ['RH', 'FINANCAS'],
      order: 5,
      route: '/configuracao',
    },
    {
      displayName: 'Comissão julgadora',
      iconName: 'menu',
      roles: ['RH', 'EXAMINADOR'],
      order: 6,
      addTopDivider: true,
      children: [
        {
          displayName: 'Cadastrar examinador Unicamp',
          iconName: 'person_add',
          roles: ['RH'],
          order: 1,
          route: '/comissao-julgadora/cadastrar-interno',
        },
        {
          displayName: 'Cadastrar examinador externo',
          iconName: 'person_add',
          roles: ['RH'],
          order: 2,
          route: '/comissao-julgadora/cadastrar-externo',
        },
        {
          displayName: 'Listar Examinadores',
          iconName: 'groups',
          roles: ['RH'],
          order: 3,
          route: '/comissao-julgadora/examinadores',
        },
        {
          displayName: 'Concursos',
          iconName: 'person_add',
          roles: ['EXAMINADOR'],
          order: 4,
          route: '/comissao-julgadora/listar-concursos',
        },
        {
          displayName: 'Recursos',
          iconName: 'gavel',
          roles: ['EXAMINADOR'],
          order: 5,
          route: '/comissao/recursos-pendentes',
          badge: 0,
        },
      ],
    },
    {
      displayName: 'Gerência',
      iconName: 'menu',
      roles: ['GERENTE', 'MULTIUNIDADES', 'SUPERADMIN', 'RH', 'DTA', 'CTU_ADMIN'],
      order: 6,
      addTopDivider: true,
      addBottomDivider: true,
      children: [
        {
          displayName: 'Consulta gerencial',
          iconName: 'summarize',
          roles: ['GERENTE'],
          order: 1,
          route: '/consulta-gerencial',
        },
        {
          displayName: 'Consulta andamento',
          iconName: 'summarize',
          roles: ['GERENTE'],
          order: 2,
          route: '/consulta-andamento',
          addBottomDivider: false,
        },
        {
          displayName: 'Consulta logs',
          iconName: 'history',
          roles: ['MULTIUNIDADES'],
          order: 3,
          route: '/consulta-log',
          addBottomDivider: true,
        },
        {
          displayName: 'Candidatos',
          iconName: 'people',
          roles: ['GERENTE'],
          order: 4,
          route: '/usuario-candidato',
        },
        {
          displayName: 'Configurações',
          iconName: 'settings',
          roles: ['MULTIUNIDADES'],
          order: 5,
          route: '/gerencia-configuracao',
        },
        {
          displayName: 'Permitir alterações',
          iconName: 'lock_open',
          roles: ['GERENTE'],
          order: 6,
          route: '/permitir-alteracao',
        },
        {
          displayName: 'Formulário complementar',
          iconName: 'format_list_numbered',
          roles: ['MULTIUNIDADES'],
          order: 6,
          route: '/formulario-complementar',
        },
        {
          displayName: 'Tipos de recursos',
          iconName: 'file_copy',
          roles: ['MULTIUNIDADES'],
          order: 7,
          route: '/tipo-recurso',
        },
        {
          displayName: 'Gestão de Usuários',
          iconName: 'manage_accounts',
          roles: ['SUPERADMIN', 'CTU_ADMIN'],
          order: 8,
          route: '/gestao-usuario',
        },
        // {
        //   displayName: 'Editar Perfis',
        //   iconName: 'person_pin',
        //   roles: ['GERENTE'],
        //   order: 8,
        //   route: '/perfil',
        //   addBottomDivider: false,
        // },
      ],
    },

    // Administradores
    {
      displayName: ' Manual do Sistema',
      iconName: 'picture_as_pdf',
      roles: ['RH'],
      order: 93,
      url: 'https://detic.unicamp.br/documento/manual-sistema-de-concursos-administrador/',
    },
    // Examinador
    {
      displayName: ' Manual do Sistema',
      iconName: 'picture_as_pdf',
      roles: ['EXAMINADOR'],
      order: 94,
      url: 'https://detic.unicamp.br/documento/manual-sistema-de-concursos-examinadores/',
    },
    {
      displayName: 'Manual DTA',
      iconName: 'picture_as_pdf',
      roles: ['DTA'],
      order: 95,
      route: '/manual/dta',
    },
    {
      displayName: 'Manual Finanças',
      iconName: 'picture_as_pdf',
      roles: ['FINANCAS'],
      order: 96,
      route: '/manual/financas',
    },
    {
      displayName: 'Manual Banca',
      iconName: 'picture_as_pdf',
      roles: ['DEPARTAMENTO'],
      order: 97,
      route: '/manual/banca',
    },
    {
      displayName: 'Manual CCPT',
      iconName: 'picture_as_pdf',
      roles: ['CCPT'],
      order: 98,
      route: '/manual/ccpt',
    },
    {
      displayName: 'Manual CACP',
      iconName: 'picture_as_pdf',
      roles: ['CACP'],
      order: 99,
      route: '/manual/cacp',
    },
    {
      displayName: 'Dúvidas',
      iconName: 'open_in_new',
      roles: [ROLE_WILDCARD_PUBLIC],
      url: 'https://unicamp-agil.atlassian.net/servicedesk/customer/portal/16/group/96/create/1896',
      order: 1002,
      addTopDivider: true,
    },
  ]

  return menu
    .filter(item => !item.hidden)
    .map(item => ({
      ...item,
      children: item.children
        ? item.children.filter(child => child.roles.some(role => perfis.includes(role) || role === '**'))
        : undefined,
    }))
    .filter(
      item =>
        item.roles.some(role => perfis.includes(role) || role === '**') || (item.children && item.children.length > 0)
    )
}
